import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { Paper, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import styles from './styles';

const useStyles = makeStyles(styles);

const DEBOUNCE_MS = 400;
const GAP = 16;
const MIN_POPUP_HEIGHT = 140;
const FALLBACK_POPUP_HEIGHT = '40vh';
const filterByLabel = createFilterOptions();
const keepAll = (options) => options;

// the popup renders inside the field's own container, where an uncapped list grows a scroller and is cut off by any other overflow
const popupCap = (field) => {
  const rect = field?.getBoundingClientRect();
  if (!rect) return FALLBACK_POPUP_HEIGHT;

  let bottom = window.innerHeight;
  for (let node = field.parentElement; node; node = node.parentElement) {
    if (window.getComputedStyle(node).overflowY !== 'visible') {
      bottom = Math.min(node.getBoundingClientRect().bottom, bottom);
      break;
    }
  }

  return Math.max(bottom - rect.bottom - GAP, MIN_POPUP_HEIGHT);
};

/**
 * Searchable, controlled select over a list of entities.
 *
 * Speaks plain ids in both directions — `value` is an id (or ids), `onChange` emits an id,
 * `null` on clear (`[]` when `multiple`) — so it drops into Formik, react-hook-form or
 * `useState` alike. Never inject a blank option: the built-in ✕ clears the field.
 *
 * `loadOptions` switches the list to async mode: fetched once on open, then debounced per
 * keystroke with stale responses dropped, while `options` still seeds the list so the
 * selected label never renders blank. Without it the list is filtered in memory,
 * case- and accent-insensitively.
 *
 * 14 props — over the lib's soft cap (README §8.8), taken deliberately so the four
 * parallel select implementations in the app can collapse into this one.
 * `clearable={false}` is for fields where "nothing selected" is not a valid state.
 * Padding passed through `classes` needs `!important`: `theme/overrides.js` sets the field
 * padding app-wide that way, and a rule can only be beaten by an equally important one.
 */
const SearchSelect = ({
  value,
  onChange,
  options,
  loadOptions,
  getOptionLabel,
  getOptionId,
  renderOption,
  footer,
  multiple,
  clearable,
  disabled,
  error,
  classes: classesProp,
  inputProps: { onBlur, ...inputProps },
}) => {
  const classes = useStyles({ classes: classesProp });

  const [loaded, setLoaded] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounce = useRef(null);
  const queryId = useRef(0);

  useEffect(() => () => clearTimeout(debounce.current), []);

  const allOptions = useMemo(() => {
    if (!loadOptions) return options;
    const seeded = new Set(options.map(getOptionId));
    return [...options, ...loaded.filter((option) => !seeded.has(getOptionId(option)))];
  }, [loadOptions, loaded, options, getOptionId]);

  const selected = useMemo(() => {
    const byId = (id) => allOptions.find((option) => getOptionId(option) === id) ?? null;
    if (!multiple) return byId(value);
    return (Array.isArray(value) ? value : []).map(byId).filter(Boolean);
  }, [allOptions, getOptionId, multiple, value]);

  const search = useCallback((query) => {
    const id = (queryId.current += 1);
    clearTimeout(debounce.current);
    setLoading(true);
    debounce.current = setTimeout(() => {
      loadOptions(query)
        .then((result) => id === queryId.current && setLoaded(result || []))
        .catch(() => {}) // reporting the failure is the caller's job
        .finally(() => id === queryId.current && setLoading(false));
    }, DEBOUNCE_MS);
  }, [loadOptions]);

  const handleChange = (event, option) => {
    if (multiple) onChange((option || []).map(getOptionId));
    else onChange(option ? getOptionId(option) : null);
  };

  const listbox = useRef(null);
  const scrolled = useRef(0);

  const trackScroll = (event) => {
    listbox.current = event.currentTarget;
    scrolled.current = event.currentTarget.scrollTop;
  };

  // MUI re-scrolls the popup when `value` changes identity, and callers rebuild their option
  // objects on every edit — deleting a row must not throw the list back to the top
  useEffect(() => {
    if (loadOptions || !listbox.current?.isConnected) return;
    listbox.current.scrollTop = scrolled.current;
  }, [options, loadOptions]);

  // read through a ref: a new PaperComponent identity remounts the popup while it is open
  const footerRef = useRef(footer);
  footerRef.current = footer;

  const field = useRef(null);
  const [maxHeight, setMaxHeight] = useState(FALLBACK_POPUP_HEIGHT);

  const PopupPaper = useCallback(({ children, ...props }) => (
    <Paper {...props} style={{ maxHeight }}>
      {children}
      {footerRef.current && (
        // a mousedown inside the popup blurs the input and closes it before the click lands
        <div className={classes.footer} onMouseDown={(event) => event.preventDefault()}>
          {footerRef.current}
        </div>
      )}
    </Paper>
  ), [classes.footer, maxHeight]);

  // lab renders the popup as the field's sibling, which a flex wrapper lays over the field; inside the root it needs no measuring
  const Popup = useCallback(({ anchorEl, open, style, ...props }) => createPortal(
    <div {...props} style={{ ...style, position: 'absolute', top: '100%', left: 0 }} />,
    field.current,
  ), []);

  return (
    <Autocomplete
      ref={field}
      classes={{
        root: classes.root,
        clearIndicatorDirty: classes.clearIndicatorDirty,
        inputRoot: classes.inputRoot,
        input: classes.input,
        endAdornment: classes.endAdornment,
        option: classes.option,
        listbox: classes.listbox,
        paper: classes.paper,
        popper: classes.popper,
      }}
      value={selected}
      options={allOptions}
      multiple={multiple}
      disableCloseOnSelect={multiple}
      disableClearable={!clearable}
      disabled={disabled}
      loading={loading}
      fullWidth
      filterOptions={loadOptions ? keepAll : filterByLabel}
      getOptionLabel={getOptionLabel}
      getOptionSelected={(option, current) => getOptionId(option) === getOptionId(current)}
      renderOption={renderOption}
      PaperComponent={PopupPaper}
      PopperComponent={Popup}
      ListboxProps={{ onScroll: trackScroll }}
      onChange={handleChange}
      // measured here, not in an effect: the popup's first paint already carries the cap, so it never shrinks in view
      onOpen={() => {
        setMaxHeight(popupCap(field.current));
        if (loadOptions && !loaded.length) search('');
      }}
      aria-expanded={true}
      onInputChange={(event, query, reason) => loadOptions && reason === 'input' && search(query)}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          error={error}
          inputProps={{
            ...params.inputProps,
            ...inputProps,
            // Autocomplete drives its own blur; the caller's (Formik `touched`) runs after it
            onBlur: (event) => {
              params.inputProps.onBlur(event);
              if (onBlur) onBlur(event);
            },
          }}
        />
      )}
    />
  );
};

SearchSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.object),
  loadOptions: PropTypes.func,
  getOptionLabel: PropTypes.func,
  getOptionId: PropTypes.func,
  renderOption: PropTypes.func,
  footer: PropTypes.node,
  multiple: PropTypes.bool,
  clearable: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  classes: PropTypes.shape({
    root: PropTypes.string,
    inputRoot: PropTypes.string,
    input: PropTypes.string,
    option: PropTypes.string,
    listbox: PropTypes.string,
    paper: PropTypes.string,
    popper: PropTypes.string,
    endAdornment: PropTypes.string,
  }),
  inputProps: PropTypes.object,
};

SearchSelect.defaultProps = {
  value: null,
  options: [],
  loadOptions: undefined,
  getOptionLabel: (option) => option?.name ?? option?.label ?? '',
  getOptionId: (option) => option?._id ?? option?.id ?? option?.value,
  renderOption: undefined,
  footer: undefined,
  multiple: false,
  clearable: true,
  disabled: false,
  error: false,
  classes: {},
  inputProps: {},
};

export default SearchSelect;
